import { RollingNumber } from './RollingNumber';
import { currencyAffix } from '../currency';
import { useIsSleek } from '../theme';

interface FmvOnlyProps {
  fmv: number;
  /** Defer the inline odometer until the block is on screen —
   *  see `RollingNumber.start`. */
  roll: boolean;
  /** Returning view — render the FMV value as static text with no
   *  roll. Forwarded to the inner `RollingNumber`. */
  instant?: boolean;
}

/**
 * Fallback two-line block used when profit is zero or negative.
 * Small caption "Fair Market Value" sitting above the big FMV
 * amount — same typography as PROFIT but a single colour.
 */
export function FmvOnly({ fmv, roll, instant = false }: FmvOnlyProps) {
  const { prefix, suffix } = currencyAffix();
  // Sleek (partner Figma 2026-10-06): the value leads, a muted label under it.
  const isSleek = useIsSleek();
  return (
    <div className={isSleek ? 'flex flex-col-reverse items-center' : 'flex flex-col items-center'}>
      <span
        data-co-component="WinnerStepFMV"
        className={isSleek ? 'font-medium tracking-[-0.15px]' : undefined}
        style={{
          color: isSleek
            ? 'var(--co-WinnerStepFMV-color, #6D707C)'
            : 'var(--co-WinnerStepFMV-color, rgba(255,255,255,0.60))',
          fontFamily:
            'var(--co-WinnerStepFMV-font, "Inter"), Inter, sans-serif',
          fontSize: 'var(--co-WinnerStepFMV-size, 14px)',
          lineHeight: '22px',
        }}
      >
        Fair Market Value
      </span>
      <span
        data-co-component="WinnerStepProfit"
        className={isSleek ? 'font-semibold tracking-[0.37px]' : 'font-bold tracking-[-0.6px]'}
        style={{
          color: 'var(--co-WinnerStepProfit-labelColor, #FFFFFF)',
          fontFamily:
            'var(--co-WinnerStepProfit-font, "Inter"), Inter, sans-serif',
          // Capped by svh so a short viewport doesn't get a banner-sized
          // number: full 44px from ~850px tall (44 / 850 ≈ 5.2svh), so on
          // phones the bottom band shrinks and the card above gets the room.
          // Sleek keeps its own band and the 700px curve (6.3svh). Unitless
          // line-height keeps the 44→56 ratio at any resolved size. Same
          // curve as stageLayout's reserves.
          fontSize: `min(var(--co-WinnerStepProfit-size, 44px), ${isSleek ? '6.3svh' : '5.2svh'})`,
          lineHeight: 1.27,
        }}
      >
        {prefix}<RollingNumber value={fmv} start={roll} instant={instant} />{suffix}
      </span>
    </div>
  );
}
